<template>
    <div :data-x="data.position.left"
         :data-y="data.position.top"
         :data-readonly="data.readonly ? '1' : '0'"
         :data-locked="data.locked ? '1' : '0'"
         :style="styles"
         class="editor-element"
         :class="{
             'selected elevation-12' : elementSelected,
             'element-noninteractive': !interactive
         }">

        <div :style="borderBoxStyle"
             class="editor-element border-box"
             :class="[elementSelected ? 'selected' : (markArea ? 'mark-area' : '')]">

            <div class="editor-element-description-container">
                <div class="editor-element-description">
                    <elementIcon :container-classes="['element-icon-container-gap', 'editor-element-icon']"
                                 :data="data" />
                    <i v-if="data.dynamic"
                       class="fas fa-hashtag" />
                    <template v-if="description">
                        {{ description }}
                    </template>
                </div>
            </div>
        </div>

        <component :is="data.type + 'Element'"
                   class="editor-element-preview"
                   :index="index"
                   :data="data"
                   :frame="frame"
                   :basic-content-styles="basicContentStyles"
                   :width="width"
                   :height="height"
                   @click="handleClick"
                   :id="'element-inner' + index">
        </component>
    </div>
</template>

<script>
    import bootstrapMixin from '../elements/_bootstrap';
    import selectMixin from '../elements/_select';
    import copyMixin from '../elements/_copy';
    import deleteMixin from '../elements/_delete';
    import resizeMixin from '../elements/_resize';
    import zindexMixin from '../elements/_zindex';
    import maximizeMixin from '../elements/_maximize';

    import textElement from '../elements/text.vue';
    import rssfeedElement from '../elements/rssfeed.vue';
    import iframeElement from '../elements/iframe.vue';
    import youtubeElement from '../elements/youtube.vue';
    import qrcodeElement from '../elements/qrcode.vue';
    import countdownElement from '../elements/countdown.vue';
    import pictureElement from '../elements/picture.vue';
    import videoElement from '../elements/video.vue';
    import pdfElement from '../elements/pdf.vue';
    import pptElement from '../elements/ppt.vue';
    import backgroundElement from '../elements/_templateBackground.vue';
    import emptyrectangleElement from '../elements/emptyrectangle.vue';
    import templateElement from '../elements/template.vue';
    import campaignElement from '../elements/campaign.vue';
    import clockElement from '../elements/clock.vue';
    import dayElement from '../elements/day.vue';
    import weatherElement from '../elements/weather.vue';
    import layerElement from '../elements/layer.vue';
    import esignElement from '../elements/esign.vue';
    import streamingElement from '../elements/streaming.vue';
    import widgetElement from '../elements/widget.vue'
    import { getDescriptionForElement } from '../../../../utils/publish'
    import elementIcon from '../elements/element-icon.vue';

    export default {
        props: {
            data: Object,
            value: Number,
            index: Number,
            frame: Object,
            basicPrivilege: Boolean,
            interactive: Boolean
        },
        mixins: [bootstrapMixin, selectMixin, copyMixin, deleteMixin, resizeMixin, zindexMixin, maximizeMixin],
        components: {
            elementIcon,
            textElement,
            rssfeedElement,
            iframeElement,
            youtubeElement,
            qrcodeElement,
            countdownElement,
            pictureElement,
            videoElement,
            pdfElement,
            pptElement,
            backgroundElement,
            emptyrectangleElement,
            templateElement,
            campaignElement,
            clockElement,
            dayElement,
            weatherElement,
            layerElement,
            esignElement,
            streamingElement,
            widgetElement,
        },
        data: function () {
            return {
                markArea: false,
            }
        },
        computed: {
            width() {
                return Math.max(this.hasMinWidth(), this.data.size.width);
            },
            height() {
                return Math.max(this.hasMinHeight(), this.data.size.height);
            },
            styles: function () {
                let transform = 'translate(' + this.data.position.left + 'px,' + this.data.position.top + 'px)';

                return {
                    webkitTransform: transform,
                    transform: transform,
                    zIndex: this.data.position.zindex,
                    width: this.width + 'px',
                    height: this.height + 'px',
                }
            },
            basicContentStyles: function () {
                return {
                    width: '100%',
                    height: '100%'
                };
            },
            borderBoxStyle: function() {
                var top = 0;
                var left = 0;
                var display = 'relative';

                return {
                    left: left + 'px',
                    top: top + 'px',
                    display: display,
                    backgroundOrigin: 'border-box',
                }
            },
            description() {
                return getDescriptionForElement(this.data)
            },
            elementSelected() {
                return this.value === this.index;
            }
        },
        methods: {
            handleClick() {
                if (this.data.locked) return null;
                return this.tap();
            }
        },
        mounted() {
            if(this.data.type === 'background') {
                this.locked = true;
            }

            switch (this.data.type) {
                case 'clock':
                case 'day':
                case 'rssfeed':
                case 'text':
                case 'weather':
                    this.markArea = true;
                    break;
                default:
                    this.markArea = false;
            }
        },
        watch: {
            width: {
                handler(val) {
                    if (!this.index || !this.elementSelected) {
                        return;
                    }
                    this.$emit('widthActive', val);
                },
                immediate: true
            },
            height: {
                handler(val) {
                    if (!this.index || !this.elementSelected) {
                        return;
                    }
                    this.$emit('heightActive', val);
                },
                immediate: true
            },
            elementSelected(val) {
                if(!this.index || !val) {
                    return;
                }
                this.$emit('widthActive', this.width);
                this.$emit('heightActive', this.height);
            }
        }
    }
</script>

<style>
.editor-element-description-container {
    position: absolute;
    left: 4px;
    bottom: 4px;
    /* font-size + 2 * padding-block */
    line-height: 16px;
    max-height: 100%;
}

.editor-element-description-container .editor-element-description {

    display: inline;
    padding: 3px 7px;
    overflow: hidden;
    overflow: clip;
    text-decoration: none;
    font-style: normal;
    font-size: 11px;
    color: #ffffff;
    /* Fallback if color-mix not supported */
    /*noinspection CssOverwrittenProperties*/
    background-color: var(--color-primary-lighter);
    /*noinspection CssOverwrittenProperties*/
    background-color: color-mix(in srgb, var(--color-primary-lighter) 50%, transparent);

    border-radius: 4px;

    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
    word-break: break-word;
}

.element-locked-icon {
    cursor: auto !important;
}
</style>
